/**
 * =============================================================================
 * PHS COMMAND CENTRE DASHBOARD - Enterprise Operational Intelligence
 * =============================================================================
 * 
 * PHASE 2B/2C: Operational Intelligence Layer + Real-Time Updates
 * 
 * Dashboard answers: "What needs attention right now?"
 * NOT: "What data exists?"
 * 
 * Components:
 * - Operational Alerts Rail (critical risk visibility)
 * - Executive KPIs (board-level metrics)
 * - SLA Heatmap (clickable risk matrix)
 * - Live Activity Feed (operational movement)
 * - Job Pipeline Board (workflow view)
 * - Real-Time Connection Status (Phase 2C)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, Filter, RefreshCw, Plus, Grid, List,
  AlertTriangle, Clock, CheckCircle, XCircle,
  ChevronDown, MoreHorizontal, Building2, Wrench, User,
  Users, FileText, Calendar, TrendingUp, X, Eye,
  MessageSquare, Phone, ArrowRight, BarChart2, Briefcase, Wifi, WifiOff
} from 'lucide-react';
import { TOKENS } from './PHSCommandLayout';
import { 
  OperationalAlertsRail, 
  SLAHeatmap, 
  ExecutiveKPIs, 
  LiveActivityFeed 
} from './OperationalIntelligence';
import { 
  ConnectionStatus, 
  useAutoRefresh,
  showRealtimeNotification 
} from './DashboardRealtime';
import { useRealtime, EVENT_TYPES } from '@/hooks/useRealtime';

const API = process.env.REACT_APP_BACKEND_URL;

// Pipeline column colors matching reference - EXPANDED for granular statuses
const PIPELINE_COLORS = {
  new_enquired: { header: '#0D9488', text: 'white' },      // Teal
  assigned: { header: '#3B82F6', text: 'white' },          // Blue
  awaiting_quote: { header: '#F97316', text: 'white' },    // Orange
  quote_awaiting_review: { header: '#EAB308', text: 'white' }, // Yellow
  quote_queried: { header: '#DC2626', text: 'white' },     // Red
  quote_sent_for_approval: { header: '#A855F7', text: 'white' }, // Purple
  quote_awaiting_approval: { header: '#A855F7', text: 'white' }, // Purple
  quote_approved: { header: '#22C55E', text: 'white' },    // Green
  quote_rejected: { header: '#DC2626', text: 'white' },    // Red
  approved: { header: '#22C55E', text: 'white' },          // Green
  po_generated: { header: '#22C55E', text: 'white' },      // Green
  po_issued: { header: '#22C55E', text: 'white' },         // Green
  scheduled: { header: '#6366F1', text: 'white' },         // Indigo
  in_progress: { header: '#3B82F6', text: 'white' },       // Blue
  awaiting_evidence: { header: '#F97316', text: 'white' }, // Orange
  sat_note_missing: { header: '#DC2626', text: 'white' },  // Red
  completed: { header: '#22C55E', text: 'white' },         // Green
  awaiting_invoice: { header: '#F97316', text: 'white' },  // Orange
  invoice_submitted: { header: '#6366F1', text: 'white' }, // Indigo
  invoice_queried: { header: '#DC2626', text: 'white' },   // Red
  invoice_approved: { header: '#22C55E', text: 'white' },  // Green
  invoiced: { header: '#6366F1', text: 'white' },          // Indigo
  ready_for_payment: { header: '#22C55E', text: 'white' }, // Green
  paid: { header: '#22C55E', text: 'white' },              // Green
  closed: { header: '#6B7280', text: 'white' },            // Grey
  cancelled: { header: '#DC2626', text: 'white' },         // Red
};

// Priority colors
const PRIORITY_COLORS = {
  emergency: { bg: '#FEE2E2', text: '#DC2626', dot: '#DC2626' },
  high: { bg: '#FEF3C7', text: '#D97706', dot: '#F59E0B' },
  medium: { bg: '#DBEAFE', text: '#2563EB', dot: '#3B82F6' },
  low: { bg: '#F3F4F6', text: '#6B7280', dot: '#9CA3AF' },
};

// =============================================================================
// TOP STATS BAR
// =============================================================================
function TopStatsBar({ stats }) {
  const statItems = [
    { 
      label: 'Total Jobs', 
      value: stats.total || 0, 
      subtext: 'All time',
      icon: Briefcase,
      color: '#6B7280'
    },
    { 
      label: 'Overdue', 
      value: stats.overdue || 0, 
      subtext: 'Require attention',
      icon: AlertTriangle,
      color: '#DC2626'
    },
    { 
      label: 'Due Today', 
      value: stats.due_today || 0, 
      subtext: 'Action required',
      icon: Calendar,
      color: '#F59E0B'
    },
    { 
      label: 'SLA Breaches', 
      value: stats.sla_breaches || 0, 
      subtext: 'Breached',
      icon: AlertTriangle,
      color: '#DC2626'
    },
    { 
      label: 'Completed (This Month)', 
      value: stats.completed_month || 0, 
      subtext: stats.completed_trend || '+0%',
      icon: CheckCircle,
      color: '#22C55E'
    },
    { 
      label: 'Avg Resolution Time', 
      value: stats.avg_resolution || '0', 
      subtext: 'Days',
      icon: Clock,
      color: '#6B7280'
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="top-stats-bar">
      {statItems.map((item, idx) => {
        const Icon = item.icon;
        return (
          <div 
            key={idx}
            className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
          >
            <div className="flex items-start justify-between mb-2">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <Icon className="w-4 h-4" style={{ color: item.color }} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-800">{item.value}</div>
            <div className="text-xs text-slate-500">{item.label}</div>
            <div className="text-xs text-slate-400 mt-1">{item.subtext}</div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// JOB PIPELINE BOARD
// =============================================================================
function JobPipelineBoard({ jobs, onJobClick, onJobSelect, loading }) {
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'table'
  
  // Group jobs by status for pipeline columns - supports all granular statuses
  const pipelineColumns = useMemo(() => {
    const columns = {
      new_enquired: { name: 'New Enquired', jobs: [], color: PIPELINE_COLORS.new_enquired },
      assigned: { name: 'Assigned', jobs: [], color: PIPELINE_COLORS.assigned },
      awaiting_quote: { name: 'Awaiting Quote', jobs: [], color: PIPELINE_COLORS.awaiting_quote },
      quote_awaiting_review: { name: 'Quote Review', jobs: [], color: PIPELINE_COLORS.quote_awaiting_review },
      quote_awaiting_approval: { name: 'Quote Approval', jobs: [], color: PIPELINE_COLORS.quote_awaiting_approval },
      approved: { name: 'Approved', jobs: [], color: PIPELINE_COLORS.approved },
      scheduled: { name: 'Scheduled', jobs: [], color: PIPELINE_COLORS.scheduled },
      in_progress: { name: 'In Progress', jobs: [], color: PIPELINE_COLORS.in_progress },
    };
    
    // Status mapping for granular statuses to pipeline columns
    // Includes legacy status support for backward compatibility
    const statusMapping = {
      // Current granular statuses
      'Job Created': 'new_enquired',
      'Assigned': 'assigned',
      'Awaiting Quote': 'awaiting_quote',
      'Quote Awaiting Review': 'quote_awaiting_review',
      'Quote Queried': 'quote_awaiting_review', // Group with review
      'Quote Sent For Approval': 'quote_awaiting_approval',
      'Quote Awaiting Approval': 'quote_awaiting_approval',
      'Quote Approved': 'approved',
      'Quote Rejected': 'awaiting_quote', // Back to quote needed
      'Approved': 'approved',
      'PO Generated': 'approved',
      'PO Issued': 'approved',
      'Scheduled': 'scheduled',
      'In Progress': 'in_progress',
      'Awaiting Evidence': 'in_progress',
      'SAT Note Missing': 'in_progress',
      // Legacy status mappings for backward compatibility
      'New': 'new_enquired',
      'Quote Received': 'quote_awaiting_review',
      'Quote Requested': 'awaiting_quote',
      'Pending': 'new_enquired',
    };
    
    (jobs || []).forEach(job => {
      const status = job.status || 'Job Created';
      const columnKey = statusMapping[status];
      
      if (columnKey && columns[columnKey]) {
        columns[columnKey].jobs.push(job);
      } else if (status === 'Job Created') {
        columns.new_enquired.jobs.push(job);
      }
      // Note: Completed, Invoiced, Paid, Closed jobs are excluded from active pipeline
    });
    
    return columns;
  }, [jobs]);

  const getTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="job-pipeline-board">
      {/* Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Job Pipeline</h2>
          <p className="text-sm text-slate-500">Real-time view of all job statuses</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('board')}
              data-testid="board-view-btn"
              className={`px-3 py-2 text-sm flex items-center gap-2 transition ${
                viewMode === 'board' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Grid className="w-4 h-4" />
              Board View
            </button>
            <button
              onClick={() => setViewMode('table')}
              data-testid="table-view-btn"
              className={`px-3 py-2 text-sm flex items-center gap-2 transition ${
                viewMode === 'table' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <List className="w-4 h-4" />
              Table View
            </button>
          </div>
          
          {/* Filters */}
          <button className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </button>
          
          {/* Refresh */}
          <button 
            onClick={() => window.location.reload()}
            className="p-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Pipeline Board */}
      {viewMode === 'board' && (
        <div className="p-4 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {Object.entries(pipelineColumns).map(([key, column]) => (
              <div key={key} className="w-56 flex-shrink-0">
                {/* Column Header */}
                <div 
                  className="rounded-t-lg px-3 py-2 flex items-center justify-between"
                  style={{ backgroundColor: column.color.header }}
                >
                  <span className="text-sm font-semibold" style={{ color: column.color.text }}>
                    {column.name.toUpperCase()}
                  </span>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full bg-white/20"
                    style={{ color: column.color.text }}
                  >
                    {column.jobs.length}
                  </span>
                </div>
                
                {/* Column Jobs */}
                <div className="bg-slate-50 rounded-b-lg p-2 space-y-2 min-h-[200px] max-h-[400px] overflow-y-auto">
                  {column.jobs.slice(0, 3).map(job => {
                    const priority = PRIORITY_COLORS[job.priority?.toLowerCase()] || PRIORITY_COLORS.medium;
                    return (
                      <div
                        key={job.id}
                        onClick={() => onJobSelect(job)}
                        className="bg-white rounded-lg p-3 border border-slate-200 cursor-pointer hover:shadow-md transition group"
                        data-testid={`pipeline-job-${job.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-semibold text-teal-600">
                            {job.job_reference || `JOB-${job.id?.slice(0,4)}`}
                          </span>
                          <button className="opacity-0 group-hover:opacity-100 transition">
                            <MoreHorizontal className="w-4 h-4 text-slate-400" />
                          </button>
                        </div>
                        <p className="text-sm text-slate-700 font-medium mb-1 line-clamp-1">
                          {job.description || job.category || 'No description'}
                        </p>
                        <p className="text-xs text-slate-500 mb-2">
                          {job.park_name || job.site_name || 'Unknown Park'}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400">
                              {job.resident_name || job.contractor_name || ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {getTimeAgo(job.created_at)}
                            </span>
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: priority.dot }}
                              title={job.priority || 'Medium'}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {column.jobs.length > 3 && (
                    <button 
                      onClick={() => onJobClick(key)}
                      className="w-full text-center py-2 text-sm text-teal-600 hover:text-teal-700"
                    >
                      + {column.jobs.length - 3} more
                    </button>
                  )}
                  
                  {column.jobs.length === 0 && (
                    <div className="text-center py-6 text-xs text-slate-400">
                      No jobs
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Table View */}
      {viewMode === 'table' && (
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">JOB ID</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">TITLE</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">PARK / PLOT</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">STATUS</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">PRIORITY</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">ASSIGNED TO</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">CREATED</th>
                </tr>
              </thead>
              <tbody>
                {jobs.slice(0, 10).map(job => {
                  const priority = PRIORITY_COLORS[job.priority?.toLowerCase()] || PRIORITY_COLORS.medium;
                  return (
                    <tr 
                      key={job.id} 
                      onClick={() => onJobSelect(job)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="py-3 px-4 font-medium text-teal-600">
                        {job.job_reference || `JOB-${job.id?.slice(0,4)}`}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {job.description || job.category || '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {job.park_name || job.site_name || '-'} / {job.plot_number || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
                          {job.status || 'New'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: priority.bg, color: priority.text }}
                        >
                          {job.priority || 'Medium'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {job.contractor_name || 'Unassigned'}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs">
                        {getTimeAgo(job.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BOTTOM PANELS
// =============================================================================
function PPMOverviewPanel({ ppmStats }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="ppm-overview-panel">
      <h3 className="font-semibold text-slate-800 mb-4">PPM Overview</h3>
      <p className="text-xs text-slate-500 mb-4">Planned Preventative Maintenance</p>
      
      {/* Donut Chart Placeholder */}
      <div className="relative w-32 h-32 mx-auto mb-4">
        <svg className="w-full h-full" viewBox="0 0 36 36">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#0D9488"
            strokeWidth="3"
            strokeDasharray={`${(ppmStats?.due_next_30 || 28) * 2}, 100`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-800">{ppmStats?.due_next_30 || 28}</span>
          <span className="text-[10px] text-slate-500">Due in Next 30 Days</span>
        </div>
      </div>
      
      {/* Legend */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-slate-600">Overdue</span>
          </div>
          <span className="font-medium text-slate-800">{ppmStats?.overdue || 4}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            <span className="text-slate-600">Due This Week</span>
          </div>
          <span className="font-medium text-slate-800">{ppmStats?.due_this_week || 8}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-teal-500"></span>
            <span className="text-slate-600">Due This Month</span>
          </div>
          <span className="font-medium text-slate-800">{ppmStats?.due_this_month || 16}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-slate-400"></span>
            <span className="text-slate-600">Not Due</span>
          </div>
          <span className="font-medium text-slate-800">{ppmStats?.not_due || 312}</span>
        </div>
      </div>
      
      <button className="w-full mt-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
        View PPM Schedule
      </button>
    </div>
  );
}

function RecentActivityPanel({ activities }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="recent-activity-panel">
      <h3 className="font-semibold text-slate-800 mb-4">Recent Activity</h3>
      
      <div className="space-y-3">
        {(activities || []).slice(0, 5).map((activity, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className="p-1.5 rounded bg-slate-100">
              <FileText className="w-3 h-3 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700 line-clamp-1">{activity.description}</p>
              <p className="text-xs text-slate-400">{activity.time_ago || '2m ago'}</p>
            </div>
          </div>
        ))}
        
        {(!activities || activities.length === 0) && (
          <div className="text-center py-4 text-sm text-slate-400">
            No recent activity
          </div>
        )}
      </div>
      
      <button className="w-full mt-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
        View All Activity
      </button>
    </div>
  );
}

function AssetsOverviewPanel({ assetStats }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="assets-overview-panel">
      <h3 className="font-semibold text-slate-800 mb-4">Assets Overview</h3>
      
      <div className="text-center mb-4">
        <p className="text-xs text-slate-500">Total Assets</p>
        <p className="text-4xl font-bold text-slate-800">{assetStats?.total || 342}</p>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-slate-600">Operational</span>
          </div>
          <span className="font-medium text-slate-800">{assetStats?.operational || 278}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            <span className="text-slate-600">Requires Attention</span>
          </div>
          <span className="font-medium text-slate-800">{assetStats?.requires_attention || 42}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-slate-600">Out of Service</span>
          </div>
          <span className="font-medium text-slate-800">{assetStats?.out_of_service || 12}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            <span className="text-slate-600">Retired</span>
          </div>
          <span className="font-medium text-slate-800">{assetStats?.retired || 10}</span>
        </div>
      </div>
      
      <button className="w-full mt-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
        View All Assets
      </button>
    </div>
  );
}

function OverdueJobsTable({ jobs, onJobClick }) {
  const overdueJobs = useMemo(() => {
    return (jobs || []).filter(j => {
      if (!j.sla_due_at) return false;
      return new Date(j.sla_due_at) < new Date();
    }).slice(0, 5);
  }, [jobs]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="overdue-jobs-table">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <h3 className="font-semibold text-slate-800">Overdue Jobs</h3>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
          {overdueJobs.length}
        </span>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">JOB ID</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">TITLE</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">PARK / PLOT</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">SLA DUE</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">OVERDUE BY</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">PRIORITY</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">ASSIGNED TO</th>
            </tr>
          </thead>
          <tbody>
            {overdueJobs.map(job => {
              const priority = PRIORITY_COLORS[job.priority?.toLowerCase()] || PRIORITY_COLORS.medium;
              const dueDate = new Date(job.sla_due_at);
              const overdueDays = Math.ceil((new Date() - dueDate) / 86400000);
              
              return (
                <tr 
                  key={job.id}
                  onClick={() => onJobClick(job)}
                  className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="py-3 px-4 font-medium text-teal-600">
                    {job.job_reference || `JOB-${job.id?.slice(0,4)}`}
                  </td>
                  <td className="py-3 px-4 text-slate-700">{job.description || job.category || '-'}</td>
                  <td className="py-3 px-4 text-slate-600">
                    {job.park_name || '-'} / {job.plot_number || '-'}
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-xs">
                    {dueDate.toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-red-600 text-xs font-medium">{overdueDays} day{overdueDays !== 1 ? 's' : ''}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: priority.bg, color: priority.text }}
                    >
                      {job.priority || 'Medium'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-600">{job.contractor_name || 'Unassigned'}</td>
                </tr>
              );
            })}
            {overdueJobs.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-slate-400 text-sm">
                  No overdue jobs
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className="p-3 border-t border-slate-100">
        <button className="text-sm text-teal-600 hover:text-teal-700">
          View all overdue jobs
        </button>
      </div>
    </div>
  );
}

function SLABreachesTable({ jobs, onJobClick }) {
  const breachedJobs = useMemo(() => {
    return (jobs || []).filter(j => {
      if (!j.sla_due_at) return false;
      return new Date(j.sla_due_at) < new Date();
    }).slice(0, 5);
  }, [jobs]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="sla-breaches-table">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <h3 className="font-semibold text-slate-800">SLA Breaches</h3>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
          {breachedJobs.length}
        </span>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">JOB ID</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">TITLE</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">SLA DUE</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">BREACHED BY</th>
              <th className="text-left py-2 px-4 text-xs text-slate-500 font-medium">PRIORITY</th>
            </tr>
          </thead>
          <tbody>
            {breachedJobs.map(job => {
              const priority = PRIORITY_COLORS[job.priority?.toLowerCase()] || PRIORITY_COLORS.medium;
              const dueDate = new Date(job.sla_due_at);
              const breachedDays = Math.ceil((new Date() - dueDate) / 86400000);
              
              return (
                <tr 
                  key={job.id}
                  onClick={() => onJobClick(job)}
                  className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="py-3 px-4 font-medium text-teal-600">
                    {job.job_reference || `JOB-${job.id?.slice(0,4)}`}
                  </td>
                  <td className="py-3 px-4 text-slate-700">{job.description || job.category || '-'}</td>
                  <td className="py-3 px-4 text-slate-600 text-xs">
                    {dueDate.toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-red-600 text-xs font-medium">{breachedDays} day{breachedDays !== 1 ? 's' : ''}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: priority.bg, color: priority.text }}
                    >
                      {job.priority || 'Medium'}
                    </span>
                  </td>
                </tr>
              );
            })}
            {breachedJobs.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-slate-400 text-sm">
                  No SLA breaches
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className="p-3 border-t border-slate-100">
        <button className="text-sm text-teal-600 hover:text-teal-700">
          View all breaches
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// JOB CARD PREVIEW PANEL (Right Sidebar)
// =============================================================================
function JobCardPreview({ job, onViewDetails, onAssign, onClose }) {
  if (!job) return null;
  
  const priority = PRIORITY_COLORS[job.priority?.toLowerCase()] || PRIORITY_COLORS.medium;
  const statusKey = (job.status || 'Job Created').toLowerCase().replace(/\s+/g, '_');
  const pipelineColor = PIPELINE_COLORS[statusKey] || PIPELINE_COLORS.new_enquired;
  
  // Progress steps
  const progressSteps = ['New Enquiry', 'Assigned', 'Quote', 'Review', 'Approval', 'Scheduled', 'Completed'];
  const currentStepIndex = 1; // TODO: Calculate based on status

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="job-card-preview">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between mb-2">
          <div>
            <span className="text-lg font-bold text-slate-800 mr-2">
              {job.job_reference || `JOB-${job.id?.slice(0,4)}`}
            </span>
            <span 
              className="px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: pipelineColor.header, color: 'white' }}
            >
              {job.status || 'New Enquired'}
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <p className="text-sm font-medium text-slate-700 mb-1">
          {job.description || job.category || 'No description'}
        </p>
        <p className="text-xs text-slate-500">
          {job.park_name || job.site_name} - Plot {job.plot_number || 'N/A'}
        </p>
        {job.priority === 'High' && (
          <span 
            className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: priority.bg, color: priority.text }}
          >
            <AlertTriangle className="w-3 h-3" />
            High Priority
          </span>
        )}
      </div>
      
      {/* Details */}
      <div className="p-4 space-y-3 text-sm border-b border-slate-100">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Reported by</span>
          <span className="text-slate-700">{job.resident_name || 'Unknown'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Reported</span>
          <span className="text-slate-700">
            {job.created_at ? new Date(job.created_at).toLocaleDateString() : '-'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Category</span>
          <span className="text-slate-700">{job.category || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Assigned to</span>
          <span className="text-slate-700">{job.contractor_name || 'Unassigned'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">SLA Due</span>
          <span className={`font-medium ${job.sla_due_at && new Date(job.sla_due_at) < new Date() ? 'text-red-600' : 'text-slate-700'}`}>
            {job.sla_due_at ? new Date(job.sla_due_at).toLocaleDateString() : '-'}
          </span>
        </div>
      </div>
      
      {/* Job Progress */}
      <div className="p-4 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-3">Job Progress</p>
        <div className="flex items-center gap-1">
          {progressSteps.map((step, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center">
              <div 
                className={`w-4 h-4 rounded-full flex items-center justify-center mb-1 ${
                  idx <= currentStepIndex ? 'bg-teal-500' : 'bg-slate-200'
                }`}
              >
                {idx <= currentStepIndex && (
                  <CheckCircle className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-[8px] text-slate-400 text-center leading-tight">{step}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Next Action */}
      <div className="p-4 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-2">Next Action</p>
        <p className="text-sm text-slate-700 mb-3">
          {job.contractor_name ? 'Chase contractor for update' : 'Assign this job to a contractor'}
        </p>
        <button 
          onClick={onAssign}
          className="w-full py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition"
        >
          {job.contractor_name ? 'Chase Contractor' : 'Assign Job'}
        </button>
      </div>
      
      {/* Actions */}
      <div className="p-4 flex gap-2">
        <button 
          onClick={onViewDetails}
          className="flex-1 py-2 border border-slate-200 text-sm text-slate-600 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
        <button className="flex-1 py-2 border border-slate-200 text-sm text-slate-600 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Add Note
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// QUICK FILTERS PANEL
// =============================================================================
function QuickFiltersPanel({ filters, onFilterClick }) {
  const filterItems = [
    { key: 'my_unassigned', label: 'My Unassigned Jobs', count: filters.my_unassigned || 22, icon: Users },
    { key: 'due_today', label: 'Jobs Due Today', count: filters.due_today || 27, icon: Calendar },
    { key: 'overdue', label: 'Overdue Jobs', count: filters.overdue || 19, icon: AlertTriangle },
    { key: 'sla_breaches', label: 'SLA Breaches', count: filters.sla_breaches || 8, icon: AlertTriangle },
    { key: 'high_priority', label: 'High Priority Jobs', count: filters.high_priority || 31, icon: AlertTriangle },
    { key: 'follow_on', label: 'Follow On Quote Required', count: filters.follow_on || 4, icon: ArrowRight },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="quick-filters-panel">
      <h3 className="font-semibold text-slate-800 mb-4">Quick Filters</h3>
      
      <div className="space-y-2">
        {filterItems.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={() => onFilterClick(item.key)}
              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition text-left"
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{item.label}</span>
              </div>
              <span className="text-sm font-medium text-slate-800">{item.count}</span>
            </button>
          );
        })}
      </div>
      
      <button className="w-full mt-4 text-sm text-red-600 hover:text-red-700">
        Clear Filters
      </button>
    </div>
  );
}

// =============================================================================
// MAIN COMMAND CENTRE DASHBOARD
// =============================================================================
export default function CommandCentreDashboard({ jobs = [], loading = false, onJobClick, onRefresh }) {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState(null);
  const [localJobs, setLocalJobs] = useState(jobs);
  const lastRefreshRef = useRef(Date.now());
  
  // Real-time connection
  const { isConnected, lastEvent, reconnect } = useRealtime('wyldecrest_helpdesk', {
    enabled: true,
    onJobCreated: (event) => {
      showRealtimeNotification(event);
      // Trigger refresh after a short delay to let backend settle
      setTimeout(() => onRefresh?.(), 500);
    },
    onJobUpdated: (event) => {
      if (event.data?.status_change) {
        showRealtimeNotification(event);
      }
      setTimeout(() => onRefresh?.(), 500);
    },
    onSlaBreach: (event) => {
      showRealtimeNotification(event);
      setTimeout(() => onRefresh?.(), 500);
    },
  });
  
  // Auto-refresh every 60 seconds when connected
  const { triggerRefresh } = useAutoRefresh(
    onRefresh || (() => {}), 
    60000, 
    isConnected
  );

  // Sync jobs from props
  useEffect(() => {
    setLocalJobs(jobs);
    lastRefreshRef.current = Date.now();
  }, [jobs]);
  
  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const overdueJobs = localJobs.filter(j => j.sla_due_at && new Date(j.sla_due_at) < now);
    const dueToday = localJobs.filter(j => {
      if (!j.sla_due_at) return false;
      const due = new Date(j.sla_due_at);
      return due.toDateString() === now.toDateString();
    });
    const completedThisMonth = localJobs.filter(j => {
      if (j.status !== 'Completed') return false;
      const completed = j.completed_at ? new Date(j.completed_at) : null;
      return completed && completed >= startOfMonth;
    });
    
    return {
      total: localJobs.length,
      overdue: overdueJobs.length,
      due_today: dueToday.length,
      sla_breaches: overdueJobs.length,
      completed_month: completedThisMonth.length,
      completed_trend: '+18% vs last month',
      avg_resolution: '3.2',
    };
  }, [localJobs]);
  
  // Mock data for panels
  const ppmStats = { due_next_30: 28, overdue: 4, due_this_week: 8, due_this_month: 16, not_due: 312 };
  const assetStats = { total: 342, operational: 278, requires_attention: 42, out_of_service: 12, retired: 10 };
  const recentActivities = [
    { description: 'JOB-2451 Quote submitted by ABC Contractors', time_ago: '2m ago' },
    { description: 'JOB-2449 Job scheduled for 28 May 2024', time_ago: '15m ago' },
    { description: 'JOB-2447 Invoice submitted', time_ago: '1h ago' },
    { description: 'JOB-2446 Payment received £1,250.00', time_ago: '2h ago' },
    { description: 'JOB-2440 Quote approved', time_ago: '3h ago' },
  ];
  const quickFilters = {
    my_unassigned: localJobs.filter(j => !j.contractor_name).length,
    due_today: stats.due_today,
    overdue: stats.overdue,
    sla_breaches: stats.sla_breaches,
    high_priority: localJobs.filter(j => j.priority?.toLowerCase() === 'high').length,
    follow_on: localJobs.filter(j => j.status === 'Follow On').length,
  };

  const handleJobSelect = (job) => {
    setSelectedJob(job);
  };

  const handleJobClick = (job) => {
    if (onJobClick) {
      onJobClick(job);
    } else {
      navigate(`/phs/helpdesk/job/${job.id}`);
    }
  };

  const handleFilterClick = (filterKey) => {
    // Navigate with filter query param for URL-based filtering
    const filterMap = {
      'my_unassigned': 'unassigned',
      'due_today': 'due_today',
      'overdue': 'overdue',
      'sla_breaches': 'sla_breached',
      'high_priority': 'high_priority',
      'follow_on': 'follow_on',
    };
    const filterValue = filterMap[filterKey] || filterKey;
    navigate(`/phs/helpdesk?filter=${filterValue}`);
  };

  return (
    <div className="space-y-6" data-testid="command-centre-dashboard">
      {/* Connection Status Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ConnectionStatus 
            isConnected={isConnected} 
            lastEvent={lastEvent}
            onReconnect={reconnect}
          />
          {lastEvent && (
            <span className="text-xs text-slate-400">
              Last update: {new Date(lastEvent.timestamp).toLocaleTimeString()}
            </span>
          )}
        </div>
        <button
          onClick={triggerRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* OPERATIONAL ALERTS RAIL - Critical risk visibility */}
      <OperationalAlertsRail 
        jobs={localJobs} 
        contractors={[]} 
        compliance={[]} 
      />
      
      {/* EXECUTIVE KPIs - Board-level metrics */}
      <ExecutiveKPIs jobs={localJobs} />
      
      {/* Main Content Grid */}
      <div className="flex gap-6">
        {/* Left Column - Pipeline & Tables */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Job Pipeline */}
          <JobPipelineBoard 
            jobs={localJobs} 
            onJobClick={(statusKey) => toast.info(`View all ${statusKey} jobs`)} 
            onJobSelect={handleJobSelect}
            loading={loading}
          />
          
          {/* SLA Heatmap & Live Activity Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SLAHeatmap jobs={localJobs} />
            <LiveActivityFeed jobs={localJobs} limit={6} />
          </div>
          
          {/* Bottom Panels Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PPMOverviewPanel ppmStats={ppmStats} />
            <RecentActivityPanel activities={recentActivities} />
            <AssetsOverviewPanel assetStats={assetStats} />
          </div>
          
          {/* Tables Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <OverdueJobsTable jobs={localJobs} onJobClick={handleJobClick} />
            <SLABreachesTable jobs={localJobs} onJobClick={handleJobClick} />
          </div>
        </div>
        
        {/* Right Sidebar - Job Preview & Filters */}
        <div className="hidden xl:block w-80 flex-shrink-0 space-y-4">
          {selectedJob ? (
            <JobCardPreview 
              job={selectedJob}
              onViewDetails={() => handleJobClick(selectedJob)}
              onAssign={() => handleJobClick(selectedJob)}
              onClose={() => setSelectedJob(null)}
            />
          ) : (
            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-6 text-center">
              <p className="text-sm text-slate-500">Select a job from the pipeline to preview details</p>
            </div>
          )}
          
          <QuickFiltersPanel filters={quickFilters} onFilterClick={handleFilterClick} />
        </div>
      </div>
    </div>
  );
}
